/**
 * Accepting an invitation.
 *
 * Reached from a link, by somebody who has no account and is not signed in. So it says what it is
 * an invitation to before asking for anything, and it asks the invitee to choose their own
 * password rather than telling them one.
 *
 * An invalid link — used, withdrawn, expired, or never real — says so in one sentence and offers
 * the sign-in page. Distinguishing those four cases on screen would be friendlier to the person
 * holding a stale link and equally friendly to somebody guessing links, so the server does not
 * distinguish them and neither does this.
 */

import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api'
import { saveSession } from '../lib/auth'
import { useI18n, LOCALES, LOCALE_LABELS } from '../lib/i18n'
import Logo from '../components/Logo'
import { GoogleSignIn } from '../components/GoogleSignIn'
import { Skeleton } from '../components/primitives'
import { TermsCheckbox } from '../components/TermsOfUse'

export default function AcceptInvite() {
  const { token = '' } = useParams()
  const { t, locale, setLocale } = useI18n()

  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Asked of a new account and of one joining with its own password alike: one box, above both forms.
  const [acceptTerms, setAcceptTerms] = useState(false)

  const { data: config } = useQuery({
    queryKey: ['auth-config'],
    queryFn: api.auth.config,
    retry: false,
    staleTime: Infinity,
  })

  const { data: invitation, isLoading, isError } = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api.invitations.preview(token),
    retry: false,
    enabled: Boolean(token),
  })

  const accept = async (googleIdToken?: string): Promise<void> => {
    setError(null)
    setBusy(true)

    try {
      const result = await api.invitations.accept({
        token,
        displayName: displayName.trim() || undefined,
        password: googleIdToken ? undefined : password,
        googleIdToken,
        acceptTerms,
      })

      saveSession(result)

      // A full navigation, not a router one.
      //
      // saveSession makes the application signed in, which swaps the whole route tree and
      // unmounts this component along with any state it was holding to decide where to go next.
      // The first version set a flag and rendered a redirect, and the flag died with the
      // component: the invitation was accepted, the member was created, and the browser sat on
      // the invitation page while its preview query refetched and reported the link as spent.
      // …and the first thing there: what only the newcomer decides (docs/refactor/18).
      window.location.replace('/dashboard?sensitive=joined')
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.code === 'invalid_credentials' ? t('invite.wrongPassword')
          : caught instanceof ApiError && caught.code === 'terms_required' ? t('terms.required')
            : caught instanceof ApiError ? caught.message : t('error.generic'),
      )
    } finally {
      setBusy(false)
    }
  }


  return (
    <div className="auth">
      <div className="auth__card">
        <div className="auth__brand">
          <Logo size={36} />
          <div>
            <div style={{ fontSize: 'var(--text-lg)', fontWeight: 650, letterSpacing: '0.08em' }}>VEPHUS</div>
            <div className="auth__tagline">{t('brand.tagline')}</div>
          </div>
        </div>

        {isLoading ? (
          <Skeleton height={160} />
        ) : isError || !invitation ? (
          <div className="stack">
            <p>{t('invite.invalid')}</p>
            <a className="btn btn--primary full" href="/sign-in">{t('auth.signIn')}</a>
          </div>
        ) : (
          <div className="stack">
            <p className="small">
              {t('invite.intro', { organization: invitation.organizationName })}
            </p>
            <p className="tiny muted">
              {invitation.email} · {t(`role.${invitation.role}`)}
            </p>

            <TermsCheckbox checked={acceptTerms} onChange={setAcceptTerms} disabled={busy} />

            {invitation.existingAccount ? (
              /*
                The address already has an account. It joins by signing in to it — its own
                password or its Google account — never by setting a new one here, which would let
                whoever holds the link into somebody else's account.
              */
              <form
                className="stack"
                onSubmit={(event) => {
                  event.preventDefault()
                  void accept()
                }}
              >
                <p className="small">{t('invite.existingAccount')}</p>
                <label className="field">
                  <span className="field__label">{t('invite.currentPassword')}</span>
                  <input
                    className="input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                </label>
                {error && <div className="field__error" role="alert">{error}</div>}
                <button type="submit" className="btn btn--primary full" disabled={busy || !acceptTerms}>
                  {busy ? t('common.saving') : t('invite.acceptExisting')}
                </button>
              </form>
            ) : (
            <form
              className="stack"
              onSubmit={(event) => {
                event.preventDefault()
                if (password !== confirm) {
                  setError(t('auth.passwordMismatch'))
                  return
                }
                void accept()
              }}
            >
              <label className="field">
                <span className="field__label">{t('auth.displayName')}</span>
                <input
                  className="input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  autoComplete="name"
                />
              </label>

              <label className="field">
                <span className="field__label">{t('invite.choosePassword')}</span>
                <input
                  className="input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={12}
                  required
                  autoComplete="new-password"
                />
                <span className="field__hint">{t('auth.passwordHint')}</span>
              </label>

              <label className="field">
                <span className="field__label">{t('auth.confirmPassword')}</span>
                <input
                  className="input"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  minLength={12}
                  required
                  autoComplete="new-password"
                />
              </label>

              {error && <div className="field__error" role="alert">{error}</div>}

              <button type="submit" className="btn btn--primary full" disabled={busy || !acceptTerms}>
                {busy ? t('common.saving') : t('invite.accept')}
              </button>
            </form>
            )}

            {/*
              Always offered, because whether the invited address is a Google one cannot be known
              from here. If it is not, the server refuses the mismatch and says so, which is a
              better outcome than hiding the option from someone who could have used it.
            */}
            <GoogleSignIn
              clientId={config?.googleClientId ?? null}
              onCredential={(idToken) => void accept(idToken)}
              disabled={busy || !acceptTerms}
            />
          </div>
        )}

        <div className="row row--between" style={{ marginTop: 'var(--s5)' }}>
          <span className="tiny muted">{t('nav.language')}</span>
          <select
            className="select"
            style={{ width: 'auto' }}
            value={locale}
            onChange={(e) => setLocale(e.target.value as typeof locale)}
          >
            {LOCALES.map((code) => (
              <option key={code} value={code}>{LOCALE_LABELS[code]}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}
